import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n';
import { API } from '../auth';

/**
 * Legal footer shown on every page (public + authenticated).
 * Reads from /api/settings/public — all data is admin-editable.
 *
 * Required by Korean PG (Toss Payments / KG Inicis / KCP):
 *  - 회사명, 대표자, 사업자등록번호, 통신판매업 신고번호,
 *    주소, 전화, 이메일 + links to 이용약관 / 개인정보처리방침 / 교환·환불정책
 */
export default function LegalFooter({ dark = true }) {
  const { t } = useLanguage();
  const [s, setS] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/settings/public`)
      .then((r) => { if (alive) setS(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const bg = dark ? 'bg-[#1A1A1A] text-[#F6F4EE]' : 'bg-[#F2EFE6] text-[#1A1A1A]';
  const muted = dark ? 'text-[#F6F4EE]/55' : 'text-[#1A1A1A]/55';
  const accent = dark ? 'text-[#C9A352]' : 'text-[#556B2F]';
  const border = dark ? 'border-[#F6F4EE]/10' : 'border-[#1A1A1A]/10';

  const rows = useMemo(() => {
    if (!s) return [];
    return [
      ['회사명', s.company_name_ko],
      ['대표자', s.representative],
      ['사업자등록번호', s.business_reg_number],
      ['통신판매업 신고번호', s.mail_order_reg_number],
      ['주소', s.address_ko],
      ['전화', s.phone],
      ['이메일', s.email],
      s.haccp_number ? ['HACCP 인증', s.haccp_number] : null,
    ].filter(Boolean);
  }, [s]);

  return (
    <footer className={`${bg} border-t ${border} mt-20`} data-testid="legal-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Legal info — required by Korean PG */}
          <div className="md:col-span-8">
            <p className={`text-xs tracking-[0.25em] uppercase ${accent} mb-4 font-semibold`}>
              {s?.company_name_en || 'Company Information'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-xs leading-relaxed">
              {rows.map(([k, v]) => (
                <div key={k} className="flex gap-2" data-testid={`footer-${k}`}>
                  <span className={`${muted} min-w-[110px]`}>{k}</span>
                  <span className="font-medium">{v || '—'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Legal links */}
          <div className="md:col-span-4">
            <p className={`text-xs tracking-[0.25em] uppercase ${accent} mb-4 font-semibold`}>
              {t('navAbout') || '정책'}
            </p>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/terms" data-testid="footer-link-terms" className="hover:underline">
                  이용약관 · Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy" data-testid="footer-link-privacy" className="hover:underline font-semibold">
                  개인정보처리방침 · Privacy
                </Link>
              </li>
              <li>
                <Link to="/refund-policy" data-testid="footer-link-refund" className="hover:underline">
                  교환 및 환불 정책 · Refund
                </Link>
              </li>
              <li>
                <Link to="/contacts" data-testid="footer-link-contacts" className="hover:underline">
                  Contact · 고객센터
                </Link>
              </li>
            </ul>
            {s?.customer_service_hours && (
              <p className={`${muted} text-xs mt-4`}>
                고객센터 운영시간<br/>
                <span className={dark ? 'text-[#F6F4EE]/80' : 'text-[#1A1A1A]/80'}>{s.customer_service_hours}</span>
              </p>
            )}
          </div>
        </div>

        <div className={`mt-8 pt-6 border-t ${border} flex flex-col md:flex-row md:items-center md:justify-between gap-2`}>
          <p className={`${muted} text-[11px]`}>
            © {new Date().getFullYear()} {s?.company_name_en || 'Craft Food Factory'}. All rights reserved.
          </p>
          <p className={`${muted} text-[11px] tracking-widest uppercase`}>
            {t('tagCraftfoodShort')}
          </p>
        </div>
      </div>
    </footer>
  );
}
